/**
 * 路由预加载工具
 * 用于优化路由切换时的资源加载体验
 */

// 存储已预加载的路由
const prefetchedRoutes = new Set<string>()

// 存储路由组件导入函数的映射
const routeComponentMap = new Map<string, () => Promise<any>>()

// 存储已预加载的资源 URL
const prefetchedResources = new Set<string>()

/**
 * 预加载路由组件及其依赖资源
 * @param routeName 路由名称
 * @param componentLoader 组件加载函数
 * @param priority 优先级：'high' 立即加载，'low' 空闲时加载
 */
export function prefetchRoute(
    routeName: string,
    componentLoader: () => Promise<any>,
    priority: 'high' | 'low' = 'low'
): void {
    if (prefetchedRoutes.has(routeName)) {
        return // 已经预加载过
    }

    const doPrefetch = async () => {
        try {
            // 预加载组件模块
            const module = await componentLoader()

            // 标记为已预加载
            prefetchedRoutes.add(routeName)

            // 尝试预加载相关的静态资源
            if (module && typeof module === 'object') {
                // Vite 会在动态导入时自动处理依赖，但我们也可以主动预加载
                // 这里主要是标记已加载，避免重复加载
            }
        } catch (error) {
            // 预加载失败不影响正常使用
            console.debug(`Prefetch failed for route ${routeName}:`, error)
        }
    }

    if (priority === 'high') {
        // 高优先级：立即预加载
        doPrefetch()
    } else {
        // 低优先级：使用 requestIdleCallback 在浏览器空闲时预加载
        if ('requestIdleCallback' in window) {
            requestIdleCallback(doPrefetch, { timeout: 2000 })
        } else {
            // 降级方案：使用 setTimeout
            setTimeout(doPrefetch, 100)
        }
    }
}

/**
 * 使用 link prefetch/preload 预加载资源
 * @param href 资源路径
 * @param as 资源类型，默认为 'script'
 * @param priority 优先级：'preload' 高优先级，'prefetch' 低优先级
 */
export function prefetchResource(
    href: string,
    as: string = 'script',
    priority: 'preload' | 'prefetch' = 'prefetch'
): void {
    // 检查是否已经添加过 prefetch/preload link
    const rel = priority === 'preload' ? 'preload' : 'prefetch'
    const existingLink = document.querySelector(`link[rel="${rel}"][href="${href}"]`)
    if (existingLink || prefetchedResources.has(href)) {
        return
    }

    const link = document.createElement('link')
    link.rel = rel
    link.href = href
    link.as = as

    // 对于 script 类型，添加 crossorigin 属性
    if (as === 'script') {
        link.crossOrigin = 'anonymous'
    }

    // 对于 preload，添加 fetchpriority
    if (priority === 'preload') {
        link.setAttribute('fetchpriority', 'high')
    }

    document.head.appendChild(link)
    prefetchedResources.add(href)
}

/**
 * 预加载路由的 JS 和 CSS 资源
 * 通过解析 Vite 构建后的 manifest 或直接预加载组件
 * @param routeName 路由名称
 * @param componentLoader 组件加载函数
 */
export async function prefetchRouteResources(
    routeName: string,
    componentLoader: () => Promise<any>
): Promise<void> {
    if (prefetchedRoutes.has(routeName)) {
        return
    }

    try {
        // 直接预加载组件模块（这会触发 Vite 的模块加载机制）
        await componentLoader()

        // 标记为已预加载
        prefetchedRoutes.add(routeName)

        // 在开发环境下，Vite 会处理模块依赖
        // 在生产环境下，我们可以通过分析已加载的 script 标签来预加载相关资源
        if (import.meta.env.PROD) {
            // 等待一小段时间，让浏览器解析模块依赖
            await new Promise(resolve => setTimeout(resolve, 50))

            // 查找所有新加载的 script 标签并预加载相关的 chunk
            const scripts = document.querySelectorAll('script[src]')
            scripts.forEach((script) => {
                const src = (script as HTMLScriptElement).src
                if (src && !prefetchedResources.has(src)) {
                    // 预加载相关的 CSS（通常 chunk 名称相同）
                    const cssHref = src.replace(/\.js$/, '.css')
                    prefetchResource(cssHref, 'style', 'prefetch')
                }
            })
        }
    } catch (error) {
        console.debug(`Failed to prefetch resources for route ${routeName}:`, error)
    }
}

/**
 * 预加载路由及其依赖
 * @param routeName 路由名称
 * @param componentLoader 组件加载函数
 */
export function prefetchRouteWithDeps(
    routeName: string,
    componentLoader: () => Promise<any>
): void {
    prefetchRoute(routeName, componentLoader)

    // 注册路由组件映射，用于后续智能预加载
    routeComponentMap.set(routeName, componentLoader)
}

/**
 * 智能预加载：根据当前路由预加载可能访问的下一个路由
 * @param currentRouteName 当前路由名称
 */
export function smartPrefetch(currentRouteName: string): void {
    // 定义路由关联关系（可能访问的下一个路由）
    const routeRelations: Record<string, string[]> = {
        Dashboard: ['Reports', 'Funnels', 'Zones'],
        Reports: ['Dashboard', 'Funnels'],
        Funnels: ['Dashboard', 'FunnelDetail'],
        FunnelDetail: ['Funnels', 'FunnelCheckoutView'],
        Zones: ['Dashboard'],
        Settings: ['Dashboard', 'AccountSettings'],
        AccountSettings: ['ShopifyTemplateSettings', 'PaymentSettings', 'IntegrationsSettings'],
        ShopifyTemplateSettings: ['PaymentSettings', 'IntegrationsSettings'],
        PaymentSettings: ['ShopifyTemplateSettings', 'AccountSettings', 'GeneralSettings'],
        IntegrationsSettings: ['AccountSettings', 'GeneralSettings'],
        GeneralSettings: ['AccountSettings'],
        Helps: ['Dashboard'],
        Subscriptions: ['SubscriptionsReports', 'SubscriptionsAnalysis']
    }

    const relatedRoutes = routeRelations[currentRouteName] || []

    relatedRoutes.forEach((routeName) => {
        const componentLoader = routeComponentMap.get(routeName)
        if (componentLoader && !prefetchedRoutes.has(routeName)) {
            // 智能预加载使用低优先级，避免影响当前路由加载
            prefetchRouteResources(routeName, componentLoader)
        }
    })
}

/**
 * 根据路由路径预加载目标路由
 * @param routePath 路由路径
 * @param routeName 路由名称（可选）
 */
export function prefetchRouteByPath(routePath: string, routeName?: string): void {
    // 路径到路由名称的映射
    const pathToNameMap: Record<string, string> = {
        '/dashboard': 'Dashboard',
        '/reports': 'Reports',
        '/zones': 'Zones',
        '/funnels': 'Funnels',
        '/helps': 'Helps',
        '/settings': 'Settings',
        '/settings/account': 'AccountSettings',
        '/settings/shopify-template': 'ShopifyTemplateSettings',
        '/settings/payment': 'PaymentSettings',
        '/settings/integrations': 'IntegrationsSettings',
        '/settings/settings': 'GeneralSettings',
        '/settings/selling-plan': 'SellingPlanSettings',
        '/settings/policy-link': 'PolicyLinkSettings',
        // '/settings/policy-management': 'PolicyManagementSettings',
        '/subscriptions/reports': 'SubscriptionsReports',
        '/subscriptions/analytics': 'SubscriptionsAnalysis',
    }

    const name = routeName || pathToNameMap[routePath]
    if (!name) {
        return
    }

    const componentLoader = routeComponentMap.get(name)
    if (componentLoader && !prefetchedRoutes.has(name)) {
        // 使用高优先级预加载，因为用户可能即将访问
        prefetchRouteResources(name, componentLoader)
    }
}

/**
 * 批量预加载路由
 * @param routes 路由配置数组
 */
export function batchPrefetchRoutes(routes: Array<{ name: string; loader: () => Promise<any> }>): void {
    routes.forEach(({ name, loader }) => {
        prefetchRouteWithDeps(name, loader)
    })
}

/**
 * 检查路由是否已预加载
 * @param routeName 路由名称
 */
export function isRoutePrefetched(routeName: string): boolean {
    return prefetchedRoutes.has(routeName)
}

